import React, { useState } from 'react';
import { X, ChevronDown, Check, Globe, Link2 } from 'lucide-react';
import { Dropdown, MenuProps, message } from 'antd';
import { UserAvatar } from './UserAvatar';
import { postLink } from './PostCard';
import { AUDIENCE_LABEL, htmlToText, type Audience, type Me, type Post } from '../socialStore';
import { feedApi } from '../socialApi';

interface ShareModalProps {
  /** Bài được chia sẻ; null = đóng */
  post: Post | null;
  me: Me;
  closeShare: () => void;
  /** Bài chia sẻ mới, để đưa lên đầu bảng tin */
  onShared: (post: Post) => void;
}

export function ShareModal({ post, me, closeShare, onShared }: ShareModalProps) {
  if (!post) return null;
  return <ShareBody key={post.id} post={post} me={me} closeShare={closeShare} onShared={onShared} />;
}

function ShareBody({ post, me, closeShare, onShared }: ShareModalProps & { post: Post }) {
  const [sending, setSending] = useState(false);
  const [audiences, setAudiences] = useState<Audience[]>(['organization']);
  const [note, setNote] = useState('');
  // chia sẻ lại một bài chia sẻ thì trỏ thẳng về bài gốc
  const originalId = post.sharedFrom || post.id;
  const preview = htmlToText(post.html);

  const toggle = (key: Audience) =>
    setAudiences(prev => (prev.includes(key) ? (prev.length > 1 ? prev.filter(x => x !== key) : prev) : [...prev, key]));

  const items: MenuProps['items'] = (Object.keys(AUDIENCE_LABEL) as Audience[]).map(key => ({
    key,
    label: <div className="flex items-center justify-between gap-4 py-1"><span>{AUDIENCE_LABEL[key]}</span>{audiences.includes(key) && <Check className="w-4 h-4 text-cyan-600" />}</div>,
    onClick: () => toggle(key),
  }));

  const share = async () => {
    if (sending) return;
    setSending(true);
    try {
      const r = await feedApi.share(originalId, note);
      onShared(r.post);
      message.success(r.message);
      closeShare();
    } catch (e: any) {
      message.error(e?.message || 'Không chia sẻ được bài viết');
    } finally {
      setSending(false);
    }
  };

  const copyLink = () =>
    navigator.clipboard?.writeText(postLink(originalId)).then(() => message.success('Đã sao chép link bài viết'), () => message.error('Trình duyệt không cho phép sao chép'));

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 dark:bg-black/70 backdrop-blur-sm p-4" onMouseDown={e => { if (e.target === e.currentTarget) closeShare(); }}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden flex flex-col max-h-[90vh]" role="dialog" aria-modal="true">
        <div className="px-4 md:px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <UserAvatar name={me.name} src={me.avatar} />
            <div>
              <span className="font-bold text-gray-900 dark:text-gray-100 block text-sm">{me.name} <span className="font-normal text-gray-500">· Chia sẻ bài viết</span></span>
              <Dropdown menu={{ items }} trigger={['click']} placement="bottomLeft">
                <button className="flex items-center space-x-1.5 text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 px-2.5 py-1 rounded-md font-medium mt-0.5 cursor-pointer">
                  <Globe className="w-3 h-3" /><span>{audiences.map(a => AUDIENCE_LABEL[a]).join(', ')}</span><ChevronDown className="w-3 h-3" />
                </button>
              </Dropdown>
            </div>
          </div>
          <button onClick={closeShare} aria-label="Đóng" className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 flex items-center justify-center cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 md:px-6 py-4 overflow-y-auto space-y-3 flex-1">
          <textarea
            autoFocus
            value={note}
            onChange={e => setNote(e.target.value)}
            maxLength={2000}
            placeholder="Nói gì đó về bài viết này..."
            rows={3}
            className="w-full bg-transparent border-none outline-none text-gray-800 dark:text-gray-200 text-base placeholder-gray-400 resize-none"
          />
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="flex items-center gap-2">
              <UserAvatar name={post.authorName} src={post.authorAvatar} size={28} />
              <span className="text-sm font-bold text-gray-900 dark:text-gray-100">{post.authorName}</span>
            </div>
            {post.title && <div className="font-bold text-gray-900 dark:text-gray-100 mt-2">{post.title}</div>}
            {preview && <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-3">{preview}</p>}
            {post.images[0] && <img src={post.images[0]} alt="" className="mt-2 rounded-md max-h-40 w-full object-cover" />}
          </div>
        </div>

        <div className="px-4 md:px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 flex gap-3 flex-shrink-0">
          <button onClick={copyLink} className="flex items-center justify-center gap-2 px-4 py-3 bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 hover:bg-gray-100 text-gray-800 dark:text-gray-100 font-bold rounded-xl cursor-pointer">
            <Link2 className="w-4 h-4" /> Sao chép link
          </button>
          <button onClick={share} disabled={sending} className="flex-1 py-3 bg-sky-900 hover:bg-sky-950 disabled:opacity-60 text-white font-bold rounded-xl shadow-md cursor-pointer">{sending ? 'Đang chia sẻ...' : 'Chia sẻ ngay'}</button>
        </div>
      </div>
    </div>
  );
}
